import React, { useState, useEffect } from 'react';
import { Menu, X, Phone, Settings } from 'lucide-react';
import { Button } from './ui/button';
import { useAuth } from '../context/AuthContext';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Both logos have solid backgrounds:
// - LOGO_WHITE_BG = Logo with white background (for scrolled/light header)
// - LOGO_BLACK_BG = Logo with black background (for hero/dark areas - blends better)
const LOGO_WHITE_BG = 'https://customer-assets.emergentagent.com/job_modern-advisors/artifacts/3cgus5vs_Shire%20Logo%20-%20small.png';
const LOGO_BLACK_BG = 'https://customer-assets.emergentagent.com/job_modern-advisors/artifacts/tpla0qf8_Shire%20Logo%20%28dark%29.png';

// Panel ID to navigation link mapping
const panelNavConfig = {
  hero: { name: 'Home', href: '#home' },
  services: { name: 'Services', href: '#services' },
  about: { name: 'About', href: '#about' },
  testimonials: { name: 'Testimonials', href: '#testimonials' },
  'case-studies': { name: 'Case Studies', href: '#case-studies' },
  investments: { name: 'Investments', href: '#investments' },
  faq: { name: 'FAQ', href: '#faq' },
  insights: { name: 'Insights', href: '#insights' },
  contact: { name: 'Contact', href: '#contact' },
};

const Header = ({ onLoginClick, onAdminClick, panels }) => {
  const { isAuthenticated, isAdmin, user } = useAuth();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const [contactInfo, setContactInfo] = useState({
    phone: "(214) 673-2710",
    phone_alt: "",
  });

  // Fetch contact info on mount. Panel layout is owned by <App> and passed
  // in via the `panels` prop — don't fetch it here, that caused a FOUC where
  // hidden nav tabs briefly appeared before the real layout arrived.
  useEffect(() => {
    const fetchContact = async () => {
      try {
        const res = await axios.get(`${API}/content/contact`);
        if (res.data) {
          setContactInfo((prev) => ({ ...prev, ...res.data }));
        }
      } catch (error) {
        // Use defaults
      }
    };
    fetchContact();
  }, []);

  // Build nav links from the panels prop. If panels is still loading (null),
  // render an empty nav so hidden sections don't flash in.
  const navLinks = (panels || [])
    .filter(panel => panel.visible)
    .sort((a, b) => a.order - b.order)
    .map(panel => {
      if (panelNavConfig[panel.id]) {
        return panelNavConfig[panel.id];
      }
      if (panel.isCustom && panel.name) {
        return { name: panel.name, href: `#${panel.id}` };
      }
      return null;
    })
    .filter(Boolean);

  const scrollToSection = (href) => {
    // If we're not on the home page, navigate there first, then scroll
    if (location.pathname !== '/') {
      navigate('/' + href);
      setIsMobileMenuOpen(false);
      return;
    }
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setIsMobileMenuOpen(false);
  };

  // When arriving at '/' with a hash, scroll to the section
  useEffect(() => {
    if (location.pathname === '/' && location.hash) {
      const el = document.querySelector(location.hash);
      if (el) {
        // slight delay so the panels mount
        setTimeout(() => el.scrollIntoView({ behavior: 'smooth' }), 200);
      }
    }
  }, [location]);

  return (
    <header
      className="fixed top-0 md:top-[var(--ticker-bar-height,68px)] left-0 right-0 z-50 bg-white shadow-lg transition-[top] duration-200"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <div className="flex-shrink-0">
            <a href="#home" onClick={() => scrollToSection('#home')} className="flex items-center">
              <img 
                src={LOGO_WHITE_BG} 
                alt="Shire Advisors" 
                className="h-10 w-auto"
              />
            </a>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <React.Fragment key={link.name}>
                <a
                  href={link.href}
                  onClick={(e) => {
                    e.preventDefault();
                    scrollToSection(link.href);
                  }}
                  className="text-sm font-medium transition-colors duration-200 hover:text-gold-600 text-navy-800"
                >
                  {link.name}
                </a>
                {link.href === '#about' && (
                  <a
                    href="#team"
                    onClick={(e) => {
                      e.preventDefault();
                      scrollToSection('#team');
                    }}
                    className="text-sm font-medium transition-colors duration-200 hover:text-gold-600 text-navy-800"
                    data-testid="nav-team-link"
                  >
                    Team
                  </a>
                )}
              </React.Fragment>
            ))}
          </nav>

          {/* CTA Button */}
          <div className="hidden lg:flex items-center gap-4">
            <a
              href={`tel:${contactInfo.phone_alt}`}
              className="flex items-center gap-2 text-sm font-medium text-navy-800 hover:text-gold-600 transition-colors duration-200"
            >
              <Phone className="w-4 h-4" />
              {contactInfo.phone}
            </a>
            <Button
              onClick={() => {
                if (isAuthenticated && isAdmin()) {
                  onAdminClick();
                } else {
                  onLoginClick();
                }
              }}
              variant="outline"
              className="border-navy-800 text-navy-800 hover:bg-navy-800 hover:text-white"
            >
              <Settings className="w-4 h-4 mr-2" />
              Clients and Staff
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? (
              <X className="w-6 h-6 text-navy-900" />
            ) : (
              <Menu className="w-6 h-6 text-navy-900" />
            )}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <div
        className={`lg:hidden absolute top-full left-0 right-0 bg-white shadow-xl transition-all duration-300 overflow-hidden ${
          isMobileMenuOpen ? 'max-h-screen opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <div className="px-4 py-6 space-y-4">
          {navLinks.map((link) => (
            <React.Fragment key={link.name}>
              <a
                href={link.href}
                onClick={(e) => {
                  e.preventDefault();
                  scrollToSection(link.href);
                }}
                className="block text-navy-800 font-medium py-2 hover:text-gold-500 transition-colors"
              >
                {link.name}
              </a>
              {link.href === '#about' && (
                <a
                  href="#team"
                  onClick={(e) => {
                    e.preventDefault();
                    scrollToSection('#team');
                  }}
                  className="block text-navy-800 font-medium py-2 hover:text-gold-500 transition-colors"
                  data-testid="mobile-nav-team-link"
                >
                  Team
                </a>
              )}
            </React.Fragment>
          ))}
          <div className="pt-4 border-t border-gray-200">
            <a
              href={`tel:${contactInfo.phone_alt}`}
              className="flex items-center gap-2 text-navy-800 font-medium py-2"
            >
              <Phone className="w-4 h-4" />
              {contactInfo.phone}
            </a>
            <Button
              onClick={() => {
                setIsMobileMenuOpen(false);
                if (isAuthenticated && isAdmin()) {
                  onAdminClick();
                } else {
                  onLoginClick();
                }
              }}
              className="w-full mt-4 bg-navy-800 hover:bg-navy-900 text-white font-semibold"
            >
              <Settings className="w-4 h-4 mr-2" />
              Clients and Staff
            </Button>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
